<template>
  <div class="ahub">
    <!-- ── Pulse: the story of the period ── -->
    <div class="pulse">
      <!-- decorations clipped in their own layer, so dropdowns can overflow the panel -->
      <div class="pulse__fx">
        <div class="pulse__orb pulse__orb--1"/>
        <div class="pulse__orb pulse__orb--2"/>
        <div class="pulse__orb pulse__orb--3"/>
        <div class="pulse__grid"/>
      </div>
      <div class="pulse__content">
        <div class="pulse__head">
          <div class="pulse__brand"><i class="fa-solid fa-chart-pie"/></div>
          <div class="pulse__text">
            <h4 class="pulse__title">{{ $t('analyticsHub.title') }}</h4>
            <p class="pulse__sub">{{ $t('analyticsHub.subtitle') }}</p>
          </div>
          <div v-if="dateRange.start" class="pulse__range">
            <i class="fa-regular fa-calendar mr-50"/>
            {{ dateRange.start | moment($t('timeFormat.date.format')) }}
            <span class="mx-25">—</span>
            {{ dateRange.end | moment($t('timeFormat.date.format')) }}
          </div>
        </div>

        <p v-if="loaded" class="pulse__story">
          {{ storyLine }}
          <span :class="attentionCount ? 'pulse__story--warn' : 'pulse__story--ok'">{{ storyAttentionLine }}</span>
        </p>

        <div v-if="loaded" class="pulse__stats">
          <div class="pulse__stat">
            <span class="pulse__stat-val tabular"><count-up :value="roomsRevenue + (journey.upsellRevenue || 0)" money/> €</span>
            <span class="pulse__stat-cap">{{ $t('analyticsHub.totalRevenue') }}</span>
          </div>
          <div class="pulse__stat">
            <span class="pulse__stat-val tabular"><count-up :value="verification.length"/></span>
            <span class="pulse__stat-cap">{{ $t('analyticsHub.arrivalsInRange') }}</span>
          </div>
          <div class="pulse__stat">
            <span class="pulse__stat-val tabular"><count-up :value="journeyCompletionRate"/>%</span>
            <span class="pulse__stat-cap">{{ $t('analyticsHub.completionRate') }}</span>
          </div>
          <div class="pulse__stat" :class="attentionCount ? 'pulse__stat--warn' : 'pulse__stat--ok'">
            <span class="pulse__stat-val tabular"><count-up :value="attentionCount"/></span>
            <span class="pulse__stat-cap">{{ $t('analyticsHub.attentionRequired') }}</span>
          </div>
        </div>

        <!-- controls docked in the masthead — the page has ONE anchor block -->
        <div class="pulse__controls">
          <calendar-bar v-model="dateRange" @input="refresh" from-key="start" until-key="end"
                        init-selection="thisMonth" :enable-custom="true"
                        :active-options="['today','yesterday','thisWeek','lastWeek','last7Days','thisMonth','lastMonth','last30Days','thisYear']"/>
          <div class="pulse__controls-row">
            <label class="ahub-filter-label mb-0 mr-50">{{ $t('contractStats.selectBuilding') }}</label>
            <building-select-box v-model="buildings" :branch-ids="getSelectedBranchesIds" append-to-body
                                 multiple clearable @input="refresh" size="sm" class="pulse__building"/>
          </div>
        </div>
      </div>
    </div>

    <!-- ── First load: skeleton cards ── -->
    <div v-if="loading && !loaded">
      <div class="ahub-grid ahub-grid--4">
        <b-skeleton v-for="n in 8" :key="n" class="ahub-skeleton-card" animation="wave"/>
      </div>
    </div>

    <div v-else :class="{'ahub-stale': loading && loaded}">
      <!-- ══ 1. Reservations ══ -->
      <section class="ahub-shell">
      <div class="ahub-section">
        <span class="ahub-section__icon" style="--tint:#7367f0;--tint-soft:rgba(115,103,240,.12)"><i class="fa-solid fa-bed"/></span>
        <span class="ahub-section__title">{{ $t('analyticsHub.reservations') }}</span>
        <span class="ahub-section__line"/>
        <span class="ahub-section__num">01</span>
      </div>
      <div class="ahub-grid ahub-grid--4">
        <div v-for="card in reservationCards" :key="card.key" v-tilt class="kpi" :class="`kpi--${card.variant}`"
             role="button" tabindex="0" @click="openDrill(card)">
          <div class="kpi__top">
            <span class="kpi__chip"><i :class="card.icon"/></span>
            <span class="kpi__label">{{ card.label }}</span>
            <i class="fa-regular fa-circle-question kpi__info" @click.stop
               v-b-tooltip.hover.bottom :title="card.info"/>
          </div>
          <div class="kpi__value tabular"><count-up :value="card.rows.length"/></div>
          <div class="kpi__foot">
            <span v-if="card.footer" class="kpi__pill">{{ card.footer }}</span>
            <span class="kpi__open"><i class="fa-solid fa-arrow-up-right-from-square mr-25"/>{{ $t('analyticsHub.viewDetails') }}</span>
          </div>
        </div>
      </div>

      <!-- ══ 2. Booking revenue ══ -->
      </section>
      <section class="ahub-shell">
      <div class="ahub-section">
        <span class="ahub-section__icon" style="--tint:#28c76f;--tint-soft:rgba(40,199,111,.12)"><i class="fa-solid fa-coins"/></span>
        <span class="ahub-section__title">{{ $t('analyticsHub.bookingRevenue') }}</span>
        <span class="ahub-section__line"/>
        <span class="ahub-section__num">02</span>
      </div>
      <div class="ahub-grid ahub-grid--3">
        <div v-for="card in bookingRevenueCards" :key="card.key" v-tilt class="kpi" :class="`kpi--${card.variant}`"
             role="button" tabindex="0" @click="openDrill(card)">
          <div class="kpi__top">
            <span class="kpi__chip"><i :class="card.icon"/></span>
            <span class="kpi__label">{{ card.label }}</span>
            <i class="fa-regular fa-circle-question kpi__info" @click.stop
               v-b-tooltip.hover.bottom :title="card.info"/>
          </div>
          <div class="kpi__value tabular">
            <template v-if="card.percent"><count-up :value="card.value"/><small class="kpi__unit">%</small></template>
            <template v-else><count-up :value="card.value" money/><small class="kpi__unit">€</small></template>
          </div>
          <div class="kpi__foot">
            <span v-if="card.footer" class="kpi__pill">{{ card.footer }}</span>
            <span class="kpi__open"><i class="fa-solid fa-arrow-up-right-from-square mr-25"/>{{ $t('analyticsHub.viewDetails') }}</span>
          </div>
        </div>
      </div>

      <!-- ══ 3. Upselling & guest journey ══ -->
      </section>
      <section class="ahub-shell">
      <div class="ahub-section">
        <span class="ahub-section__icon" style="--tint:#a9863f;--tint-soft:rgba(194,170,142,.22)"><i class="fa-solid fa-gift"/></span>
        <span class="ahub-section__title">{{ $t('analyticsHub.upsellSection') }}</span>
        <span class="ahub-section__line"/>
        <span class="ahub-section__num">03</span>
      </div>
      <div class="ahub-grid ahub-grid--4">
        <div v-for="card in upsellCards" :key="card.key" v-tilt class="kpi" :class="`kpi--${card.variant}`"
             role="button" tabindex="0" @click="openDrill(card)">
          <div class="kpi__top">
            <span class="kpi__chip"><i :class="card.icon"/></span>
            <span class="kpi__label">{{ card.label }}</span>
            <i class="fa-regular fa-circle-question kpi__info" @click.stop
               v-b-tooltip.hover.bottom :title="card.info"/>
          </div>
          <div class="kpi__value tabular">
            <template v-if="card.percent"><count-up :value="card.value"/><small class="kpi__unit">%</small></template>
            <template v-else><count-up :value="card.value" money/><small class="kpi__unit">€</small></template>
          </div>
          <div class="kpi__foot">
            <span v-if="card.footer" class="kpi__pill">{{ card.footer }}</span>
            <span class="kpi__open"><i class="fa-solid fa-arrow-up-right-from-square mr-25"/>{{ $t('analyticsHub.viewDetails') }}</span>
          </div>
        </div>
      </div>
      <b-row class="ahub-row mt-1">
        <b-col cols="12" lg="5" class="d-flex">
          <div class="ahub-panel flex-fill">
            <div class="ahub-panel__head">{{ $t('analyticsHub.journeyFunnel') }}</div>
            <div class="ahub-journey">
              <div class="ahub-donut" :style="{'--pct': journeyCompletionRate}">
                <div class="ahub-donut__hole">
                  <span class="ahub-donut__val tabular">{{ journeyCompletionRate }}%</span>
                  <span class="ahub-donut__cap">{{ $t('analyticsHub.completionRate') }}</span>
                </div>
              </div>
              <div class="ahub-funnel">
                <div v-for="step in journeyFunnel" :key="step.label" class="ahub-funnel__row">
                  <span class="ahub-funnel__label">{{ step.label }}</span>
                  <div class="ahub-funnel__track">
                    <div class="ahub-funnel__bar" :style="{width: step.pct + '%', background: step.color}"/>
                  </div>
                  <span class="ahub-funnel__val tabular">{{ step.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col cols="12" lg="7" class="d-flex">
          <div class="ahub-panel flex-fill">
            <div class="ahub-panel__head">
              {{ $t('analyticsHub.topProducts') }}
              <small class="text-muted ml-50">{{ $t('analyticsHub.topProductsHint') }}</small>
            </div>
            <div v-if="topProducts.length" class="ahub-products">
              <div v-for="(product, index) in topProducts" :key="product.name" class="ahub-product">
                <span class="ahub-rank" :class="`ahub-rank--${index + 1}`">{{ index + 1 }}</span>
                <div class="ahub-product__mid">
                  <div class="ahub-product__row">
                    <span class="ahub-product__name">{{ product.name }}</span>
                    <span class="ahub-product__count">× {{ product.count }}</span>
                    <span class="ahub-product__rev tabular">{{ product.revenue | numberFormatter }} €</span>
                  </div>
                  <div class="ahub-product__track">
                    <div class="ahub-product__bar" :style="{width: productShare(product) + '%'}"/>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="ahub-empty">{{ $t('nodatafound') }}</div>
          </div>
        </b-col>
      </b-row>

      <!-- ══ 4. Verification & readiness ══ -->
      </section>
      <section class="ahub-shell">
      <div class="ahub-section">
        <span class="ahub-section__icon" style="--tint:#ff9f43;--tint-soft:rgba(255,159,67,.12)"><i class="fa-solid fa-user-check"/></span>
        <span class="ahub-section__title">{{ $t('analyticsHub.verificationSection') }}</span>
        <span class="ahub-section__line"/>
        <span class="ahub-section__num">04</span>
      </div>
      <div class="ahub-grid ahub-grid--4">
        <div v-for="card in verificationCards" :key="card.key" v-tilt class="kpi" :class="`kpi--${card.variant}`"
             role="button" tabindex="0" @click="openDrill(card)">
          <div class="kpi__top">
            <span class="kpi__chip"><i :class="card.icon"/></span>
            <span class="kpi__label">{{ card.label }}</span>
            <i class="fa-regular fa-circle-question kpi__info" @click.stop
               v-b-tooltip.hover.bottom :title="card.info"/>
          </div>
          <div class="kpi__value tabular">{{ card.value }}</div>
          <div class="kpi__foot">
            <span v-if="card.footer" class="kpi__pill">{{ card.footer }}</span>
            <span class="kpi__open"><i class="fa-solid fa-arrow-up-right-from-square mr-25"/>{{ $t('analyticsHub.viewDetails') }}</span>
          </div>
        </div>
      </div>

      <div class="ahub-panel">
        <div class="ahub-panel__head ahub-panel__head--split">
          <span>
            {{ $t('analyticsHub.guestSteps') }}
            <b-badge variant="light-primary" class="ml-50 tabular">{{ verificationFiltered.length }}</b-badge>
          </span>
          <b-input-group size="sm" class="ahub-search">
            <b-input-group-prepend is-text><i class="fa-solid fa-magnifying-glass"/></b-input-group-prepend>
            <b-form-input v-model="guestSearch" :placeholder="$t('analyticsHub.searchGuest')" debounce="150"/>
          </b-input-group>
        </div>
        <template v-if="verificationFiltered.length">
          <b-table-simple small responsive class="ahub-table mb-0">
            <b-thead>
              <b-tr>
                <b-th>{{ $t('analyticsHub.guest') }}</b-th>
                <b-th>{{ $t('analyticsHub.property') }}</b-th>
                <b-th>{{ $t('analyticsHub.checkinDate') }}</b-th>
                <b-th>{{ $t('analyticsHub.steps') }}</b-th>
                <b-th class="text-center">{{ $t('analyticsHub.ready') }}</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="guest in verificationPaged" :key="guest.contractId" class="ahub-row-link"
                    @click="openContract(guest.contractId)">
                <b-td>
                  <span class="ahub-guest">
                    <span class="ahub-avatar" :class="guest.ready ? 'ahub-avatar--ok' : 'ahub-avatar--warn'">{{ initials(guest.clientName) }}</span>
                    <span class="font-weight-bold">{{ guest.clientName || '—' }}</span>
                  </span>
                </b-td>
                <b-td class="text-truncate" style="max-width:220px">{{ guest.propertyAddress || '—' }}</b-td>
                <b-td class="tabular">{{ guest.moveDate | moment($t('timeFormat.date.format')) }}</b-td>
                <b-td>
                  <span v-for="step in guestSteps(guest)" :key="step.key" class="ahub-chip"
                        :class="step.done ? 'ahub-chip--done' : 'ahub-chip--open'"
                        v-b-tooltip.hover :title="step.done ? $t('analyticsHub.stepDone') : $t('analyticsHub.stepOpen')">
                    <i :class="step.done ? 'fa-solid fa-check' : 'fa-regular fa-clock'" class="mr-25"/>{{ step.label }}
                  </span>
                </b-td>
                <b-td class="text-center">
                  <b-badge :variant="guest.ready ? 'light-success' : 'light-warning'">
                    {{ guest.ready ? $t('analyticsHub.readyYes') : $t('analyticsHub.readyNo') }}
                  </b-badge>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <div class="ahub-pager">
            <b-pagination v-model="guestPage" :total-rows="verificationFiltered.length" :per-page="guestPerPage"
                          size="sm" class="mb-0" align="right"/>
          </div>
        </template>
        <div v-else class="ahub-empty">{{ $t('nodatafound') }}</div>
      </div>

      <!-- ══ 5. Reception (lazy — Twilio answers slowly) ══ -->
      </section>
      <section class="ahub-shell">
      <div class="ahub-section">
        <span class="ahub-section__icon" style="--tint:#ea5455;--tint-soft:rgba(234,84,85,.12)"><i class="fa-solid fa-headset"/></span>
        <span class="ahub-section__title">{{ $t('analyticsHub.receptionSection') }}</span>
        <span class="ahub-section__line"/>
        <span class="ahub-section__num">05</span>
      </div>
      <div class="ahub-grid ahub-grid--4">
        <template v-if="receptionLoading">
          <b-skeleton v-for="n in 4" :key="'r' + n" class="ahub-skeleton-card" animation="wave"/>
        </template>
        <template v-else>
          <div v-for="card in receptionCards" :key="card.key" v-tilt class="kpi" :class="[`kpi--${card.variant}`, card.rows ? '' : 'kpi--static']"
               :role="card.rows ? 'button' : null" @click="card.rows ? openDrill(card) : null">
            <div class="kpi__top">
              <span class="kpi__chip"><i :class="card.icon"/></span>
              <span class="kpi__label">{{ card.label }}</span>
              <i class="fa-regular fa-circle-question kpi__info" @click.stop
                 v-b-tooltip.hover.bottom :title="card.info"/>
            </div>
            <div class="kpi__value tabular">{{ card.value }}</div>
            <div class="kpi__foot">
              <span v-if="card.footer" class="kpi__pill">{{ card.footer }}</span>
              <span v-if="card.rows" class="kpi__open"><i class="fa-solid fa-arrow-up-right-from-square mr-25"/>{{ $t('analyticsHub.viewDetails') }}</span>
            </div>
          </div>
        </template>
      </div>
      </section>
    </div>

    <!-- ── Drill-down modal, custom design ── -->
    <b-modal v-model="drill.open" size="lg" centered hide-header hide-footer scrollable
             body-class="p-0" content-class="ahub-modal" dialog-class="ahub-modal-dialog">
      <div class="ahub-modal__inner" :style="{'--tint': drill.tint, '--tint-soft': drill.tintSoft}">
        <!-- header -->
        <div class="ahub-modal__head">
          <span class="ahub-modal__icon"><i :class="drill.icon"/></span>
          <div class="ahub-modal__titles">
            <div class="ahub-modal__title">{{ drill.title }}</div>
            <div class="ahub-modal__sub">
              <i class="fa-regular fa-calendar mr-25"/>
              {{ dateRange.start | moment($t('timeFormat.date.format')) }} — {{ dateRange.end | moment($t('timeFormat.date.format')) }}
            </div>
          </div>
          <span class="ahub-modal__count">
            <b class="tabular">{{ drill.rows.length }}</b> {{ drillNoun }}
            <template v-if="drillTotal > 0"> · <b class="tabular">{{ drillTotal | numberFormatter }} €</b></template>
          </span>
          <button type="button" class="ahub-modal__close" @click="drill.open = false"><i class="fa-solid fa-xmark"/></button>
        </div>

        <!-- body -->
        <div class="ahub-modal__body">
          <!-- reservations -->
          <template v-if="drill.mode === 'reservations'">
            <div v-for="row in drillPaged" :key="row.contractId" class="ahub-mrow" @click="openContract(row.contractId)">
              <span class="ahub-avatar ahub-avatar--tint">{{ initials(row.clientName) }}</span>
              <div class="ahub-mrow__mid">
                <div class="ahub-mrow__name">{{ row.clientName || '—' }}</div>
                <div class="ahub-mrow__sub">{{ row.propertyAddress || '—' }}</div>
              </div>
              <div class="ahub-mrow__dates tabular">
                {{ row.moveDate | moment($t('timeFormat.date.format')) }}
                <i class="fa-solid fa-arrow-right-long mx-25"/>
                {{ row.endDate | moment($t('timeFormat.date.format')) }}
              </div>
              <platform-logo :platform="row.platform || 'Treasure Home'" size="15px" class="ahub-mrow__platform"/>
              <span v-if="row.amount != null" class="ahub-mrow__amt tabular">{{ (row.amount || 0) | numberFormatter }} €</span>
              <i class="fa-solid fa-chevron-right ahub-mrow__go"/>
            </div>
          </template>

          <!-- product orders -->
          <template v-else-if="drill.mode === 'products'">
            <div v-for="(row, index) in drillPaged" :key="row.contractId + '-' + index" class="ahub-mrow" @click="openContract(row.contractId)">
              <span class="ahub-mrow__prodicon"><i class="fa-solid fa-gift"/></span>
              <div class="ahub-mrow__mid">
                <div class="ahub-mrow__name">{{ row.name || '—' }}</div>
                <div class="ahub-mrow__sub">{{ row.clientName || '—' }}<template v-if="row.propertyAddress"> · {{ row.propertyAddress }}</template></div>
              </div>
              <span class="ahub-status" :class="row.status === 'PAID' ? 'ahub-status--paid' : 'ahub-status--requested'">
                {{ row.status === 'PAID' ? $t('analyticsHub.statusPaid') : $t('analyticsHub.statusRequested') }}
              </span>
              <span class="ahub-mrow__dates tabular">{{ row.requestTime | moment($t('timeFormat.date.format')) }}</span>
              <span class="ahub-mrow__amt tabular">{{ (row.price || 0) | numberFormatter }} €</span>
              <i class="fa-solid fa-chevron-right ahub-mrow__go"/>
            </div>
          </template>

          <!-- calls -->
          <template v-else-if="drill.mode === 'calls'">
            <div v-for="(row, index) in drillPaged" :key="'call' + index" class="ahub-mrow ahub-mrow--plain">
              <span class="ahub-mrow__prodicon">
                <i :class="isOutbound(row.direction) ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'"/>
              </span>
              <div class="ahub-mrow__mid">
                <div class="ahub-mrow__name tabular">{{ row.from || '—' }} <i class="fa-solid fa-arrow-right-long mx-25" style="font-size:.7rem;opacity:.5"/> {{ row.to || '—' }}</div>
                <div class="ahub-mrow__sub tabular">{{ row.startTime | moment('DD.MM.YYYY HH:mm') }}<template v-if="row.duration"> · {{ row.duration }}s</template></div>
              </div>
              <span class="ahub-status" :class="row.status === 'completed' || row.status === 'in-progress' ? 'ahub-status--paid' : 'ahub-status--requested'">
                {{ row.status || '—' }}
              </span>
            </div>
          </template>

          <!-- sms -->
          <template v-else-if="drill.mode === 'sms'">
            <div v-for="(row, index) in drillPaged" :key="'sms' + index" class="ahub-mrow ahub-mrow--plain ahub-mrow--tall">
              <span class="ahub-mrow__prodicon"><i class="fa-solid fa-comment-sms"/></span>
              <div class="ahub-mrow__mid">
                <div class="ahub-mrow__name tabular">{{ row.to || '—' }}</div>
                <div class="ahub-mrow__msg">{{ row.message || '—' }}</div>
              </div>
              <span class="ahub-mrow__dates tabular">{{ row.sendDate | moment('DD.MM.YYYY HH:mm') }}</span>
            </div>
          </template>

          <!-- whatsapp -->
          <template v-else-if="drill.mode === 'whatsapp'">
            <div v-for="(row, index) in drillPaged" :key="'wa' + index" class="ahub-mrow ahub-mrow--tall"
                 :class="row.contractId ? '' : 'ahub-mrow--plain'"
                 @click="row.contractId ? openContract(row.contractId) : null">
              <span class="ahub-mrow__prodicon">
                <i :class="row.incoming ? 'fa-solid fa-arrow-down' : 'fa-solid fa-arrow-up'"/>
              </span>
              <div class="ahub-mrow__mid">
                <div class="ahub-mrow__name">{{ row.customerName || '—' }}</div>
                <div class="ahub-mrow__msg">{{ row.body || '—' }}</div>
              </div>
              <span class="ahub-mrow__dates tabular">{{ row.date | moment('DD.MM.YYYY HH:mm') }}</span>
              <i v-if="row.contractId" class="fa-solid fa-chevron-right ahub-mrow__go"/>
            </div>
          </template>

          <!-- guests + steps -->
          <template v-else>
            <div v-for="row in drillPaged" :key="row.contractId" class="ahub-mrow ahub-mrow--tall" @click="openContract(row.contractId)">
              <span class="ahub-avatar" :class="row.ready ? 'ahub-avatar--ok' : 'ahub-avatar--warn'">{{ initials(row.clientName) }}</span>
              <div class="ahub-mrow__mid">
                <div class="ahub-mrow__name">{{ row.clientName || '—' }}</div>
                <div class="ahub-mrow__sub">
                  {{ row.propertyAddress || '—' }} · <span class="tabular">{{ row.moveDate | moment($t('timeFormat.date.format')) }}</span>
                </div>
                <div class="ahub-mrow__chips">
                  <span v-for="step in guestSteps(row)" :key="step.key" class="ahub-chip"
                        :class="step.done ? 'ahub-chip--done' : 'ahub-chip--open'">
                    <i :class="step.done ? 'fa-solid fa-check' : 'fa-regular fa-clock'" class="mr-25"/>{{ step.label }}
                  </span>
                </div>
              </div>
              <i class="fa-solid fa-chevron-right ahub-mrow__go"/>
            </div>
          </template>

          <div v-if="!drill.rows.length" class="ahub-empty">{{ $t('nodatafound') }}</div>
        </div>

        <!-- footer -->
        <div v-if="drill.rows.length > drillPerPage" class="ahub-modal__foot">
          <b-pagination v-model="drillPage" :total-rows="drill.rows.length" :per-page="drillPerPage"
                        size="sm" class="mb-0" align="right"/>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import CalendarBar from '@/components/widgets/calendar/CalendarBar.vue'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'
import PlatformLogo from '@/components/widgets/PlatformLogo.vue'

const emptyData = () => ({
  Contracts: {checkin: [], checkout: [], staying: [], cancelled: []},
  NewBookings: [],
  Journey: {started: 0, submitted: 0, upsellRevenue: 0, upsellPending: 0, upsellContracts: 0, topProducts: [], items: []},
  Verification: [],
})
const emptyReception = () => ({calls: null, callsAnswered: null, callsMissed: null, sms: null, whatsapp: null, avgResponseSeconds: null})

/**
 * Animated count-up number: eases from the previous value to the new one.
 * Respects prefers-reduced-motion.
 */
const CountUp = {
  name: 'CountUp',
  props: {value: {type: Number, default: 0}, money: {type: Boolean, default: false}, duration: {type: Number, default: 900}},
  data: () => ({display: 0, raf: 0}),
  watch: {
    value: {immediate: true, handler(next) { this.animate(this.display, next || 0) }},
  },
  beforeDestroy() { cancelAnimationFrame(this.raf) },
  methods: {
    animate(from, to) {
      cancelAnimationFrame(this.raf)
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this.display = to; return }
      const startTs = performance.now()
      const duration = this.duration
      const step = ts => {
        const progress = Math.min((ts - startTs) / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        this.display = from + (to - from) * eased
        if (progress < 1) this.raf = requestAnimationFrame(step)
        else this.display = to
      }
      this.raf = requestAnimationFrame(step)
    },
  },
  render(h) {
    const formatter = this.$options.filters && this.$options.filters.numberFormatter
    const text = this.money
      ? (formatter ? formatter(this.display) : this.display.toFixed(2))
      : String(Math.round(this.display))
    return h('span', text)
  },
}

/**
 * 3D tilt: the card rotates toward the cursor with a light glare following it
 * (see .kpi::after). Pure CSS transforms — no library, disabled for
 * prefers-reduced-motion.
 */
const tilt = {
  bind(el) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    el.__tiltMove = e => {
      const rect = el.getBoundingClientRect()
      const px = (e.clientX - rect.left) / rect.width
      const py = (e.clientY - rect.top) / rect.height
      const rx = (0.5 - py) * 8
      const ry = (px - 0.5) * 10
      el.style.transform = `perspective(700px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translateY(-4px) scale(1.015)`
      el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`)
      el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`)
    }
    el.__tiltLeave = () => { el.style.transform = '' }
    el.addEventListener('mousemove', el.__tiltMove)
    el.addEventListener('mouseleave', el.__tiltLeave)
  },
  unbind(el) {
    el.removeEventListener('mousemove', el.__tiltMove)
    el.removeEventListener('mouseleave', el.__tiltLeave)
  },
}

/* card colour identities — passed into the modal so it wears the card's tint */
const TINTS = {
  primary: ['#7367f0', 'rgba(115,103,240,.12)'],
  success: ['#28c76f', 'rgba(40,199,111,.12)'],
  warning: ['#ff9f43', 'rgba(255,159,67,.14)'],
  danger: ['#ea5455', 'rgba(234,84,85,.12)'],
  txf: ['#00cfe8', 'rgba(0,207,232,.12)'],
  pending: ['#9c6bfa', 'rgba(156,107,250,.12)'],
  gold: ['#a9863f', 'rgba(194,170,142,.22)'],
}

export default {
  name: 'AnalyticsHub',
  components: {CalendarBar, BuildingSelectBox, PlatformLogo, CountUp},
  directives: {tilt},
  data: () => ({
    dateRange: {},
    buildings: [],
    guestSearch: '',
    guestPage: 1,
    guestPerPage: 8,
    drillPage: 1,
    drillPerPage: 10,
    loading: true,
    loaded: false,
    pendingFetch: false,
    inflight: false,
    receptionLoading: true,
    data: emptyData(),
    receptionData: emptyReception(),
    drill: {open: false, title: '', icon: '', tint: '#7367f0', tintSoft: 'rgba(115,103,240,.12)', mode: 'reservations', rows: [], monetary: false},
  }),
  created() { this.$root.$on('crm::branch-updated', this.refresh) },
  beforeDestroy() { this.$root.$off('crm::branch-updated', this.refresh) },
  watch: {
    guestSearch() { this.guestPage = 1 },
  },
  computed: {
    ...mapGetters('data', ['getSelectedBranchesIds']),
    contracts() { return this.data.Contracts || emptyData().Contracts },
    journey() { return this.data.Journey || emptyData().Journey },
    verification() { return this.data.Verification || [] },
    reception() { return this.receptionData || emptyReception() },
    upsellItems() { return this.journey.items || [] },

    roomsRevenue() { return (this.contracts.checkin || []).reduce((sum, c) => sum + (c.amount || 0), 0) },
    attentionCount() { return this.verification.filter(v => !v.ready).length },
    storyLine() {
      const fmt = this.$options.filters.numberFormatter
      return this.$t('analyticsHub.story', {
        arrivals: this.verification.length,
        ready: this.verification.filter(v => v.ready).length,
        revenue: `${fmt(this.roomsRevenue + (this.journey.upsellRevenue || 0))} €`,
        upsell: `${fmt(this.journey.upsellRevenue || 0)} €`,
      })
    },
    storyAttentionLine() {
      return this.attentionCount
        ? this.$t('analyticsHub.storyAttention', {n: this.attentionCount})
        : this.$t('analyticsHub.storyAllGood')
    },
    cancelledValue() { return (this.contracts.cancelled || []).reduce((sum, c) => sum + (c.amount || 0), 0) },
    drillTotal() {
      if (this.drill.mode === 'products') return this.drill.rows.reduce((sum, r) => sum + (r.price || 0), 0)
      return this.drill.monetary ? this.drill.rows.reduce((sum, r) => sum + (r.amount || 0), 0) : 0
    },
    drillNoun() {
      if (this.drill.mode === 'products') return this.$t('analyticsHub.wordProducts')
      if (this.drill.mode === 'verification') return this.$t('analyticsHub.wordGuests')
      if (this.drill.mode === 'calls') return this.$t('analyticsHub.wordCalls')
      if (this.drill.mode === 'sms' || this.drill.mode === 'whatsapp') return this.$t('analyticsHub.wordMessages')
      return this.$t('analyticsHub.reservations')
    },
    drillPaged() {
      const from = (this.drillPage - 1) * this.drillPerPage
      return this.drill.rows.slice(from, from + this.drillPerPage)
    },
    verificationFiltered() {
      const term = (this.guestSearch || '').trim().toLowerCase()
      if (!term) return this.verification
      return this.verification.filter(v =>
        (v.clientName || '').toLowerCase().includes(term) || (v.propertyAddress || '').toLowerCase().includes(term))
    },
    verificationPaged() {
      const from = (this.guestPage - 1) * this.guestPerPage
      return this.verificationFiltered.slice(from, from + this.guestPerPage)
    },

    reservationCards() {
      return [
        {key: 'checkin', variant: 'primary', icon: 'fas fa-sign-in-alt', rows: this.contracts.checkin || [],
          mode: 'reservations', monetary: true,
          label: this.$t('analyticsHub.checkins'), info: this.$t('analyticsHub.checkinsInfo')},
        {key: 'checkout', variant: 'warning', icon: 'fas fa-sign-out-alt', rows: this.contracts.checkout || [],
          mode: 'reservations', monetary: true,
          label: this.$t('analyticsHub.checkouts'), info: this.$t('analyticsHub.checkoutsInfo')},
        {key: 'new', variant: 'success', icon: 'fa-solid fa-calendar-plus', rows: this.data.NewBookings || [],
          mode: 'reservations', monetary: true,
          label: this.$t('analyticsHub.newBookings'), info: this.$t('analyticsHub.newBookingsInfo')},
        {key: 'cancelled', variant: 'danger', icon: 'fas fa-times-circle', rows: this.contracts.cancelled || [],
          mode: 'reservations', monetary: true,
          footer: this.cancelledValue > 0 ? `${this.$options.filters.numberFormatter(this.cancelledValue)} € ${this.$t('analyticsHub.lostValue')}` : null,
          info: this.$t('analyticsHub.cancellationsInfo'), label: this.$t('analyticsHub.cancellations')},
      ]
    },
    bookingRevenueCards() {
      const checkins = (this.contracts.checkin || []).length
      return [
        {key: 'rooms', variant: 'success', icon: 'fa-solid fa-bed', value: this.roomsRevenue,
          rows: this.contracts.checkin || [], mode: 'reservations', monetary: true,
          label: this.$t('analyticsHub.roomRevenue'), info: this.$t('analyticsHub.roomRevenueInfo')},
        {key: 'perCheckin', variant: 'primary', icon: 'fa-solid fa-calculator',
          value: checkins ? +(this.roomsRevenue / checkins).toFixed(2) : 0,
          rows: this.contracts.checkin || [], mode: 'reservations', monetary: true,
          label: this.$t('analyticsHub.revenuePerCheckin'), info: this.$t('analyticsHub.revenuePerCheckinInfo')},
        {key: 'total', variant: 'pending', icon: 'fa-solid fa-landmark',
          value: this.roomsRevenue + (this.journey.upsellRevenue || 0),
          rows: this.contracts.checkin || [], mode: 'reservations', monetary: true,
          label: this.$t('analyticsHub.totalRevenue'), info: this.$t('analyticsHub.totalRevenueInfo')},
      ]
    },
    upsellCards() {
      const checkins = (this.contracts.checkin || []).length
      const conversion = checkins ? Math.round((this.journey.upsellContracts || 0) / checkins * 100) : 0
      const paidItems = this.upsellItems.filter(i => i.status === 'PAID')
      const requestedItems = this.upsellItems.filter(i => i.status !== 'PAID')
      return [
        {key: 'upsell', variant: 'gold', icon: 'fa-solid fa-gift', value: this.journey.upsellRevenue || 0,
          rows: paidItems, mode: 'products',
          label: this.$t('analyticsHub.upsellRevenue'), info: this.$t('analyticsHub.upsellRevenueInfo')},
        {key: 'upsellPending', variant: 'warning', icon: 'fa-solid fa-hourglass-half',
          value: this.journey.upsellPending || 0,
          rows: requestedItems, mode: 'products',
          label: this.$t('analyticsHub.upsellPendingCard'), info: this.$t('analyticsHub.upsellPendingCardInfo')},
        {key: 'conversion', variant: 'txf', icon: 'fa-solid fa-percent', value: conversion, percent: true,
          rows: this.upsellItems, mode: 'products',
          footer: `${this.journey.upsellContracts || 0} / ${checkins}`,
          label: this.$t('analyticsHub.upsellConversion'), info: this.$t('analyticsHub.upsellConversionInfo')},
        {key: 'perBooking', variant: 'pending', icon: 'fa-solid fa-scale-balanced',
          value: checkins ? +( (this.journey.upsellRevenue || 0) / checkins ).toFixed(2) : 0,
          rows: paidItems, mode: 'products',
          label: this.$t('analyticsHub.upsellPerBooking'), info: this.$t('analyticsHub.upsellPerBookingInfo')},
      ]
    },
    journeyFunnel() {
      const started = this.journey.started || 0
      const finished = this.journey.submitted || 0
      const stopped = Math.max(started - finished, 0)
      const max = Math.max(started, 1)
      return [
        {label: this.$t('analyticsHub.journeyStarted'), value: started, pct: Math.round(started / max * 100), color: 'linear-gradient(90deg,#7367f0,#9c8cfc)'},
        {label: this.$t('analyticsHub.journeyStopped'), value: stopped, pct: Math.round(stopped / max * 100), color: 'linear-gradient(90deg,#ff9f43,#ffc085)'},
        {label: this.$t('analyticsHub.journeyFinished'), value: finished, pct: Math.round(finished / max * 100), color: 'linear-gradient(90deg,#28c76f,#6fe0a8)'},
      ]
    },
    journeyCompletionRate() {
      const started = this.journey.started || 0
      return started ? Math.round((this.journey.submitted || 0) / started * 100) : 0
    },
    topProducts() { return (this.journey.topProducts || []).slice(0, 5) },
    verificationCards() {
      const total = this.verification.length
      const readyRows = this.verification.filter(v => v.ready)
      const verifiedRows = this.verification.filter(v => v.verified)
      const journeyRows = this.verification.filter(v => v.journeyStatus === 'SUBMITTED')
      const missingRows = this.verification.filter(v => !v.ready)
      return [
        {key: 'onlineCheckin', variant: 'primary', icon: 'fa-solid fa-mobile-screen-button',
          value: total ? `${Math.round(journeyRows.length / total * 100)}%` : '—',
          rows: journeyRows, mode: 'verification',
          footer: `${journeyRows.length} / ${total}`,
          label: this.$t('analyticsHub.onlineCheckinRate'), info: this.$t('analyticsHub.onlineCheckinRateInfo')},
        {key: 'verified', variant: 'txf', icon: 'fa-solid fa-id-card',
          value: total ? `${Math.round(verifiedRows.length / total * 100)}%` : '—',
          rows: verifiedRows, mode: 'verification',
          footer: `${verifiedRows.length} / ${total}`,
          label: this.$t('analyticsHub.verificationRate'), info: this.$t('analyticsHub.verificationRateInfo')},
        {key: 'ready', variant: 'success', icon: 'fa-solid fa-circle-check',
          value: total ? `${Math.round(readyRows.length / total * 100)}%` : '—',
          rows: readyRows, mode: 'verification',
          footer: `${readyRows.length} / ${total}`,
          label: this.$t('analyticsHub.readyToArrive'), info: this.$t('analyticsHub.readyToArriveInfo')},
        {key: 'missing', variant: 'danger', icon: 'fa-solid fa-triangle-exclamation', value: missingRows.length,
          rows: missingRows, mode: 'verification',
          label: this.$t('analyticsHub.attentionRequired'), info: this.$t('analyticsHub.attentionRequiredInfo')},
      ]
    },
    receptionCards() {
      const fmtCount = v => (v === null || v === undefined) ? '—' : v
      const answered = this.reception.callsAnswered
      const missed = this.reception.callsMissed
      let callsFooter = null
      if (answered !== null && answered !== undefined) {
        callsFooter = `${answered} ${this.$t('analyticsHub.answered')}` + ((missed || 0) > 0 ? ` · ${missed} ${this.$t('analyticsHub.missed')}` : '')
      }
      return [
        {key: 'calls', variant: 'primary', icon: 'fa-solid fa-phone', value: fmtCount(this.reception.calls),
          footer: callsFooter, rows: this.reception.callList || [], mode: 'calls',
          label: this.$t('analyticsHub.calls'), info: this.$t('analyticsHub.callsInfo')},
        {key: 'sms', variant: 'warning', icon: 'fa-solid fa-comment-sms', value: fmtCount(this.reception.sms),
          rows: this.reception.smsList || [], mode: 'sms',
          label: this.$t('analyticsHub.smsMessages'), info: this.$t('analyticsHub.smsMessagesInfo')},
        {key: 'whatsapp', variant: 'success', icon: 'fab fa-whatsapp', value: fmtCount(this.reception.whatsapp),
          rows: this.reception.whatsappList || [], mode: 'whatsapp',
          label: this.$t('analyticsHub.whatsappMessages'), info: this.$t('analyticsHub.whatsappMessagesInfo')},
        {key: 'response', variant: 'pending', icon: 'fa-solid fa-stopwatch',
          value: this.reception.avgResponseSeconds != null ? this.formatDuration(this.reception.avgResponseSeconds) : '—',
          label: this.$t('analyticsHub.avgResponseTime'), info: this.$t('analyticsHub.avgResponseTimeInfo')},
      ]
    },
  },
  methods: {
    ...mapActions('realEstateContractsStats', ['getAnalytics', 'getAnalyticsReception']),
    refresh() {
      // coalesce bursts: range + building change can fire together
      if (this.inflight) { this.pendingFetch = true; return }
      this.doFetch()
    },
    doFetch() {
      if (!this.dateRange.start) return
      const request = {...this.dateRange, buildings: this.buildings}
      this.inflight = true
      this.loading = true
      this.guestPage = 1

      // reception is fetched in PARALLEL and rendered when it lands — Twilio
      // is queried live and must never hold up the rest of the page
      this.receptionLoading = true
      this.getAnalyticsReception(request)
        .then(data => { this.receptionData = {...emptyReception(), ...((data || {}).Reception || {})} })
        .catch(() => { this.receptionData = emptyReception() })
        .finally(() => { this.receptionLoading = false })

      this.getAnalytics(request)
        .then(data => {
          this.data = {...emptyData(), ...(data || {})}
          this.loaded = true
        })
        .finally(() => {
          this.inflight = false
          this.loading = false
          if (this.pendingFetch) { this.pendingFetch = false; this.doFetch() }
        })
    },
    openDrill(card) {
      const [tint, tintSoft] = TINTS[card.variant] || TINTS.primary
      this.drillPage = 1
      this.drill = {
        open: true,
        title: card.label,
        icon: card.icon,
        tint, tintSoft,
        mode: card.mode || 'reservations',
        rows: card.rows || [],
        monetary: !!card.monetary,
      }
    },
    openContract(contractId) {
      if (!contractId) return
      const route = this.$router.resolve({name: 'Edit Real estate rent contract', params: {id: contractId}})
      window.open(route.href, '_blank')
    },
    guestSteps(guest) {
      return [
        {key: 'verified', label: this.$t('analyticsHub.stepVerified'), done: !!guest.verified},
        {key: 'signed', label: this.$t('analyticsHub.stepSigned'), done: !!guest.signed},
        {key: 'paid', label: this.$t('analyticsHub.stepPaid'), done: !!guest.paid},
        {key: 'journey', label: this.$t('analyticsHub.stepJourney'), done: guest.journeyStatus === 'SUBMITTED'},
      ]
    },
    initials(name) {
      if (!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()
    },
    isOutbound(direction) {
      return (direction || '').indexOf('outbound') !== -1
    },
    productShare(product) {
      const top = Math.max(...this.topProducts.map(p => p.revenue || 0), 1)
      return Math.max(Math.round((product.revenue || 0) / top * 100), 3)
    },
    formatDuration(seconds) {
      if (seconds < 60) return `${Math.round(seconds)} s`
      const minutes = Math.floor(seconds / 60)
      return `${minutes}:${String(Math.round(seconds % 60)).padStart(2, '0')} min`
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/* ── Page canvas: soft colour washes behind everything, so the sections sit
     ON something instead of floating in the void ── */
.ahub {
  position: relative;
  &::before {
    content: ''; position: absolute; inset: -16px -16px 0; pointer-events: none;
    background:
      radial-gradient(560px 300px at 8% 0%, rgba(115,103,240,.07), transparent 70%),
      radial-gradient(520px 280px at 92% 10%, rgba(194,170,142,.10), transparent 70%),
      radial-gradient(520px 320px at 50% 100%, rgba(0,207,232,.05), transparent 72%);
  }
  > * { position: relative; }
}

/* ── Section shells: each chapter of the story lives in its own glass band ── */
.ahub-shell {
  background: rgba(255,255,255,.55);
  border: 1px solid rgba(15,23,42,.05);
  border-radius: 22px;
  padding: 18px 20px 20px;
  margin-bottom: 18px;
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 2px rgba(15,23,42,.03);
}
.ahub-shell .ahub-section { margin: 0 0 16px; }
.ahub-shell .ahub-grid { margin-bottom: 0; }
.ahub-shell .ahub-grid + .ahub-panel { margin-top: 18px; }
.ahub-shell .ahub-panel { margin-bottom: 0; box-shadow: none; }
.ahub-shell .ahub-row { row-gap: 16px; }

/* ── Pulse: the story panel ── */
.pulse {
  /* NO overflow:hidden here — the calendar dropdown must escape the panel.
     z-index lifts it above the shells (their backdrop-filter makes them
     stacking contexts that would otherwise paint over the dropdown). */
  position: relative; z-index: 20; border-radius: 20px; margin-bottom: 22px;
  background: linear-gradient(125deg, #10172c 0%, #1c2b4f 52%, #4d4030 130%);
  box-shadow: 0 24px 60px -24px rgba(10,14,35,.55);
}
.pulse__fx { position: absolute; inset: 0; overflow: hidden; border-radius: inherit; pointer-events: none; }
.pulse__orb {
  position: absolute; border-radius: 50%; filter: blur(46px); opacity: .5; pointer-events: none;
  animation: pulse-float 9s ease-in-out infinite;
}
.pulse__orb--1 { width: 260px; height: 260px; top: -110px; right: -50px; background: radial-gradient(circle, rgba(194,170,142,.85), transparent 70%); }
.pulse__orb--2 { width: 200px; height: 200px; bottom: -100px; left: 22%; background: radial-gradient(circle, rgba(115,103,240,.7), transparent 70%); animation-delay: -3s; }
.pulse__orb--3 { width: 160px; height: 160px; top: -60px; left: -40px; background: radial-gradient(circle, rgba(0,207,232,.55), transparent 70%); animation-delay: -6s; }
@keyframes pulse-float {
  0%, 100% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(18px) translateX(-14px); }
}
.pulse__grid {
  position: absolute; inset: 0; opacity: .22; pointer-events: none;
  background-image: radial-gradient(rgba(255,255,255,.4) 1px, transparent 1px);
  background-size: 24px 24px;
  -webkit-mask-image: linear-gradient(115deg, transparent 10%, #000 45%, transparent 90%);
  mask-image: linear-gradient(115deg, transparent 10%, #000 45%, transparent 90%);
}
.pulse__content { position: relative; padding: 22px 28px 20px; }
.pulse__head { display: flex; align-items: center; gap: 15px; }
.pulse__brand {
  width: 48px; height: 48px; border-radius: 15px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.14); backdrop-filter: blur(8px); font-size: 1.2rem; color: #eddcb8;
  border: 1px solid rgba(255,255,255,.18);
  box-shadow: 0 10px 24px -10px rgba(0,0,0,.5);
}
.pulse__text { flex: 1; min-width: 0; }
.pulse__title { font-weight: 800; font-size: 1.2rem; margin: 0; color: #fff; letter-spacing: -.01em; }
.pulse__sub { font-size: .78rem; color: rgba(255,255,255,.62); margin: 0; }
.pulse__range {
  display: none; align-items: center; font-size: .8rem; font-weight: 600; color: #eddcb8;
  padding: 7px 14px; border-radius: 999px; background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.14);
  white-space: nowrap;
  @media (min-width: 768px) { display: inline-flex; }
}
.pulse__story {
  margin: 16px 2px 0; max-width: 860px;
  font-size: .98rem; line-height: 1.65; color: rgba(255,255,255,.88); font-weight: 500;
}
.pulse__story--warn { color: #ffc085; font-weight: 700; }
.pulse__story--ok { color: #7fe0ae; font-weight: 700; }
.pulse__stats { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 16px; }
.pulse__stat {
  flex: 1 1 150px; min-width: 140px;
  padding: 12px 16px; border-radius: 14px;
  background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.12);
  backdrop-filter: blur(8px);
  transition: transform .2s ease, background .2s ease;
  &:hover { transform: translateY(-3px); background: rgba(255,255,255,.12); }
}
.pulse__stat-val { display: block; font-size: 1.35rem; font-weight: 800; color: #fff; line-height: 1.15; white-space: nowrap; }
.pulse__stat-cap { display: block; font-size: .64rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: rgba(255,255,255,.55); margin-top: 3px; }
.pulse__stat--warn { border-color: rgba(255,159,67,.5); .pulse__stat-val { color: #ffc085; } }
.pulse__stat--ok { border-color: rgba(40,199,111,.45); .pulse__stat-val { color: #7fe0ae; } }
.pulse__controls {
  margin-top: 18px; padding: 14px 16px 10px;
  background: rgba(252,252,254,.96); border-radius: 16px;
  box-shadow: 0 16px 34px -20px rgba(0,0,0,.55);
}
.pulse__controls-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.pulse__building { flex: 1 1 240px; max-width: 420px; }

.ahub-filter-label { font-size: .72rem; font-weight: 700; text-transform: uppercase; color: #64748b; letter-spacing: .04em; display: block; }

/* ── Sections ── */
.ahub-section { display: flex; align-items: center; gap: 10px; margin: 34px 0 16px; }
.ahub-section__num {
  font-size: 1.35rem; font-weight: 800; letter-spacing: -.02em;
  color: rgba(100,116,139,.22); line-height: 1; user-select: none;
}
.ahub-section__icon {
  width: 30px; height: 30px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center;
  background: var(--tint-soft, rgba(115,103,240,.12));
  color: var(--tint); font-size: .8rem;
}
.ahub-section__title { font-size: .82rem; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #475569; white-space: nowrap; }
.ahub-section__line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(100,116,139,.22), transparent); }
.ahub-stale { opacity: .5; pointer-events: none; transition: opacity .2s; }

/* ── Grid & skeletons ── */
.ahub-grid { display: grid; gap: 20px; margin-bottom: 4px; }
.ahub-grid--3 { grid-template-columns: repeat(3, 1fr); }
.ahub-grid--4 { grid-template-columns: repeat(4, 1fr); }
.ahub-grid--5 { grid-template-columns: repeat(5, 1fr); }
@media (max-width: 1450px) { .ahub-grid--5 { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 1199px) { .ahub-grid--3, .ahub-grid--4 { grid-template-columns: repeat(2, 1fr); } .ahub-grid--5 { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 575px) { .ahub-grid--3, .ahub-grid--4, .ahub-grid--5 { grid-template-columns: 1fr; } }
.ahub-skeleton-card { height: 122px; border-radius: 16px; margin: 0; }
.ahub-row { margin-top: 2px; }

/* ── KPI card v3: clean surface, tinted accents ── */
.kpi {
  --tint: #7367f0;
  --tint-soft: rgba(115,103,240,.12);
  position: relative; overflow: hidden;
  background: #fff;
  border: 1px solid rgba(15,23,42,.06);
  border-radius: 16px;
  padding: 16px 18px 14px;
  min-height: 122px;
  display: flex; flex-direction: column;
  box-shadow: 0 2px 10px rgba(15,23,42,.04);
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  cursor: pointer;

  /* 3D: the v-tilt directive drives transform; children float on layers */
  transform-style: preserve-3d;
  will-change: transform;

  &::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--tint), transparent 70%);
  }
  /* light glare that follows the cursor (--mx/--my set by v-tilt) */
  &::after {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,.5), transparent 46%);
    opacity: 0; transition: opacity .25s ease;
  }
  &:hover {
    border-color: var(--tint);
    box-shadow: 0 24px 44px -18px var(--tint-soft), 0 16px 34px -18px rgba(15,23,42,.3);
    &::after { opacity: .55; }
  }
  &:focus { outline: none; }

  &__top, &__value, &__foot { transform: translateZ(24px); }
  &__chip { box-shadow: 0 6px 14px -6px var(--tint-soft); }
  &--static { cursor: default; }
  &--static:hover { transform: none; border-color: rgba(15,23,42,.06); box-shadow: 0 2px 10px rgba(15,23,42,.04); }

  &__top { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
  &__chip {
    width: 36px; height: 36px; border-radius: 11px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--tint-soft); color: var(--tint); font-size: .95rem;
  }
  &__label {
    flex: 1; min-width: 0;
    font-size: .73rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
    color: #64748b; line-height: 1.25;
  }
  &__info { font-size: .78rem; color: #94a3b8; cursor: help; transition: color .15s; align-self: flex-start; &:hover { color: #475569; } }
  &__value {
    font-size: 1.7rem; font-weight: 800; line-height: 1.15; letter-spacing: -.01em; color: #1f2a44;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__unit { font-size: .95rem; font-weight: 700; color: var(--tint); margin-left: 3px; }
  &__foot { margin-top: auto; padding-top: 8px; display: flex; align-items: center; gap: 8px; min-height: 24px; }
  &__pill {
    font-size: .68rem; font-weight: 700; padding: 3px 9px; border-radius: 999px;
    background: var(--tint-soft); color: var(--tint);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__open {
    margin-left: auto; font-size: .64rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
    color: var(--tint); opacity: 0; transform: translateX(4px); transition: opacity .18s, transform .18s; white-space: nowrap;
  }
  &:hover &__open { opacity: 1; transform: none; }

  &--primary { --tint: #7367f0; --tint-soft: rgba(115,103,240,.12); }
  &--success { --tint: #28c76f; --tint-soft: rgba(40,199,111,.12); }
  &--warning { --tint: #ff9f43; --tint-soft: rgba(255,159,67,.14); }
  &--danger  { --tint: #ea5455; --tint-soft: rgba(234,84,85,.12); }
  &--txf     { --tint: #00cfe8; --tint-soft: rgba(0,207,232,.12); }
  &--pending { --tint: #9c6bfa; --tint-soft: rgba(156,107,250,.12); }
  &--gold    { --tint: #a9863f; --tint-soft: rgba(194,170,142,.22); }
}

/* ── Panels ── */
.ahub-panel {
  background: #fff; border-radius: 16px; padding: 18px 22px; margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(15,23,42,.04); border: 1px solid rgba(15,23,42,.06);
}
.ahub-panel__head { font-size: .85rem; font-weight: 800; color: #334155; margin-bottom: 14px; }
.ahub-panel__head--split { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.ahub-search { max-width: 260px; }
.ahub-empty { text-align: center; color: #94a3b8; padding: 26px 0; font-size: .85rem; }
.ahub-pager { display: flex; justify-content: flex-end; padding-top: 12px; border-top: 1px solid rgba(15,23,42,.06); margin-top: 10px; }

/* ── Journey: donut + funnel ── */
.ahub-journey { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
.ahub-donut {
  --pct: 0;
  width: 128px; height: 128px; border-radius: 50%; flex-shrink: 0;
  background: conic-gradient(#28c76f calc(var(--pct) * 1%), #eef2f7 0);
  display: flex; align-items: center; justify-content: center;
}
.ahub-donut__hole {
  width: 96px; height: 96px; border-radius: 50%; background: #fff;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  box-shadow: inset 0 2px 6px rgba(20,24,50,.06);
}
.ahub-donut__val { font-size: 1.35rem; font-weight: 800; color: #1f2a44; line-height: 1; }
.ahub-donut__cap { font-size: .58rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; margin-top: 4px; text-align: center; max-width: 84px; }
.ahub-funnel { flex: 1; min-width: 220px; display: grid; gap: 14px; }
.ahub-funnel__row { display: grid; grid-template-columns: 92px 1fr 46px; align-items: center; gap: 10px; }
.ahub-funnel__label { font-size: .76rem; font-weight: 700; color: #64748b; }
.ahub-funnel__track { height: 14px; background: #f1f5f9; border-radius: 7px; overflow: hidden; }
.ahub-funnel__bar { height: 100%; border-radius: 7px; transition: width .45s ease; min-width: 4px; }
.ahub-funnel__val { font-size: .9rem; font-weight: 800; text-align: right; color: #334155; }

/* ── Top products ── */
.ahub-products { display: grid; gap: 14px; }
.ahub-product { display: flex; align-items: center; gap: 12px; }
.ahub-product__mid { flex: 1; min-width: 0; }
.ahub-product__row { display: flex; align-items: baseline; gap: 8px; margin-bottom: 5px; }
.ahub-product__name { font-size: .85rem; font-weight: 700; color: #1f2a44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ahub-product__count { font-size: .74rem; color: #94a3b8; font-weight: 600; white-space: nowrap; }
.ahub-product__rev { margin-left: auto; font-size: .85rem; font-weight: 800; color: #1f9d57; white-space: nowrap; }
.ahub-product__track { height: 7px; border-radius: 4px; background: #f1f5f9; overflow: hidden; }
.ahub-product__bar { height: 100%; border-radius: 4px; background: linear-gradient(90deg, #c2aa8e, #a9863f); transition: width .45s ease; }
.ahub-rank {
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 24px; height: 24px; border-radius: 50%; font-size: .74rem; font-weight: 800;
  background: #f1f5f9; color: #64748b;
  &--1 { background: linear-gradient(135deg,#fbbf24,#d97706); color: #fff; }
  &--2 { background: linear-gradient(135deg,#cbd5e1,#94a3b8); color: #fff; }
  &--3 { background: linear-gradient(135deg,#f0abfc,#c084fc); color: #fff; }
}

/* ── Tables / guests ── */
.ahub-table { font-size: .85rem; }
.ahub-table td { vertical-align: middle; }
.ahub-row-link { cursor: pointer; &:hover { background: rgba(115,103,240,.05); } }
.ahub-guest { display: inline-flex; align-items: center; gap: 8px; }
.ahub-avatar {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: .68rem; font-weight: 800; color: #fff;
  &--ok { background: linear-gradient(135deg,#34d399,#059669); }
  &--warn { background: linear-gradient(135deg,#fbbf24,#d97706); }
  &--tint { background: linear-gradient(135deg, var(--tint, #7367f0), #4b3fd1); }
}
.ahub-chip {
  display: inline-flex; align-items: center;
  font-size: .68rem; font-weight: 700; letter-spacing: .02em;
  border-radius: 999px; padding: 3px 9px; margin: 2px 4px 2px 0;
  &--done { background: rgba(40,199,111,.12); color: #1f9d57; }
  &--open { background: rgba(255,159,67,.14); color: #b26a12; }
}
</style>

<style lang="scss">
/* ══ Drill modal — custom skin, deliberately NOT the system default ══ */
.ahub-modal-dialog .modal-content, .ahub-modal {
  border: none !important; border-radius: 20px !important; overflow: hidden;
  box-shadow: 0 30px 90px -20px rgba(10,14,35,.45) !important;
}
.ahub-modal__inner { display: flex; flex-direction: column; max-height: calc(100vh - 7rem); }
.ahub-modal__head {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 22px;
  background:
    radial-gradient(420px 120px at 0% 0%, var(--tint-soft), transparent 70%),
    linear-gradient(120deg, rgba(20,28,51,.03), transparent);
  border-bottom: 1px solid rgba(15,23,42,.07);
}
.ahub-modal__icon {
  width: 44px; height: 44px; border-radius: 13px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--tint-soft); color: var(--tint); font-size: 1.05rem;
}
.ahub-modal__titles { flex: 1; min-width: 0; }
.ahub-modal__title { font-size: 1.02rem; font-weight: 800; color: #1f2a44; line-height: 1.2; }
.ahub-modal__sub { font-size: .75rem; color: #94a3b8; font-weight: 600; margin-top: 2px; }
.ahub-modal__count {
  font-size: .78rem; color: #475569; font-weight: 600; white-space: nowrap;
  padding: 6px 12px; border-radius: 999px; background: rgba(15,23,42,.05);
  b { color: var(--tint); }
}
.ahub-modal__close {
  width: 34px; height: 34px; border: none; border-radius: 50%; flex-shrink: 0;
  background: rgba(15,23,42,.06); color: #64748b; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s, transform .15s;
  &:hover { background: rgba(234,84,85,.14); color: #ea5455; transform: rotate(90deg); }
  &:focus { outline: none; }
}
.ahub-modal__body { padding: 10px 12px; overflow-y: auto; }
.ahub-modal__foot { padding: 12px 18px; border-top: 1px solid rgba(15,23,42,.07); background: rgba(15,23,42,.02); }

/* modal rows */
.ahub-mrow {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 12px; border-radius: 12px; cursor: pointer;
  transition: background .15s, transform .15s;
  &:hover { background: var(--tint-soft, rgba(115,103,240,.08)); .ahub-mrow__go { opacity: 1; transform: none; } }
  & + & { margin-top: 2px; }
}
.ahub-mrow--tall { align-items: flex-start; }
.ahub-mrow--plain { cursor: default; }
.ahub-mrow__msg {
  font-size: .78rem; color: #64748b; margin-top: 2px; line-height: 1.45;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.ahub-mrow__mid { flex: 1; min-width: 0; }
.ahub-mrow__name { font-size: .88rem; font-weight: 700; color: #1f2a44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ahub-mrow__sub { font-size: .74rem; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.ahub-mrow__chips { margin-top: 6px; }
.ahub-mrow__dates { font-size: .76rem; color: #64748b; font-weight: 600; white-space: nowrap; }
.ahub-mrow__platform { flex-shrink: 0; }
.ahub-mrow__amt { font-size: .88rem; font-weight: 800; color: #1f2a44; white-space: nowrap; min-width: 74px; text-align: right; }
.ahub-mrow__go { font-size: .7rem; color: var(--tint, #7367f0); opacity: 0; transform: translateX(-4px); transition: opacity .15s, transform .15s; }
.ahub-mrow__prodicon {
  width: 34px; height: 34px; border-radius: 11px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--tint-soft); color: var(--tint); font-size: .85rem;
}
.ahub-status {
  font-size: .66rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
  padding: 3px 9px; border-radius: 999px; white-space: nowrap;
  &--paid { background: rgba(40,199,111,.14); color: #1f9d57; }
  &--requested { background: rgba(255,159,67,.16); color: #b26a12; }
}
@media (max-width: 640px) {
  .ahub-mrow__dates, .ahub-mrow__platform { display: none; }
  .ahub-modal__count { display: none; }
}

/* dark mode */
.dark-layout {
  .ahub-shell { background: rgba(40,48,70,.45); border-color: rgba(255,255,255,.05); }
  .ahub-section__num { color: rgba(255,255,255,.12); }
  .pulse__controls { background: #283046; box-shadow: 0 16px 34px -20px rgba(0,0,0,.7); }
  .ahub-panel { background: #283046; border-color: rgba(255,255,255,.06); }
  .ahub-panel__head { color: #d0d2d6; }
  .ahub-section__title { color: #b4b7bd; }
  .ahub-section__line { background: linear-gradient(90deg, rgba(255,255,255,.14), transparent); }
  .kpi {
    background: #283046; border-color: rgba(255,255,255,.07);
    box-shadow: 0 2px 10px rgba(0,0,0,.2);
    &__label { color: #94a3b8; }
    &__value { color: #e9ecf2; }
    &::after { background: radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,.14), transparent 46%); }
    &--static:hover { border-color: rgba(255,255,255,.07); box-shadow: 0 2px 10px rgba(0,0,0,.2); }
  }
  .ahub-pager { border-color: rgba(255,255,255,.08); }
  .ahub-funnel__track, .ahub-product__track { background: #1e2a3e; }
  .ahub-funnel__label { color: #94a3b8; }
  .ahub-funnel__val, .ahub-product__name { color: #e2e8f0; }
  .ahub-donut { background: conic-gradient(#28c76f calc(var(--pct) * 1%), #1e2a3e 0); }
  .ahub-donut__hole { background: #283046; box-shadow: inset 0 2px 6px rgba(0,0,0,.3); }
  .ahub-donut__val { color: #e2e8f0; }
  .ahub-rank { background: #1e2a3e; color: #94a3b8; }
  .ahub-row-link:hover { background: rgba(115,103,240,.12); }
  .ahub-empty { color: #676d7d; }
  .ahub-skeleton-card { background: #1e2a3e; }

  /* modal */
  .ahub-modal-dialog .modal-content, .ahub-modal { background: #283046 !important; }
  .ahub-modal__head { background: radial-gradient(420px 120px at 0% 0%, var(--tint-soft), transparent 70%); border-color: rgba(255,255,255,.08); }
  .ahub-modal__title { color: #e9ecf2; }
  .ahub-modal__count { background: rgba(255,255,255,.06); color: #b4b7bd; }
  .ahub-modal__close { background: rgba(255,255,255,.08); color: #94a3b8; }
  .ahub-modal__foot { background: rgba(0,0,0,.12); border-color: rgba(255,255,255,.08); }
  .ahub-mrow__name, .ahub-mrow__amt { color: #e9ecf2; }
  .ahub-mrow__dates { color: #94a3b8; }
}
</style>
